import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useDatasetViewerCitationPresentation } from "src/reusable/Chat/citations/CitationPresentationContext";
import { useGetDatasetType } from "src/views/datasets/services/DatasetServices";
import { useSubsetDetailedPOCRecords } from "../../cap-trending/trendingServices";
import { useAutomationRecordDetail } from "../services/AutomationRecordServices";
import { PredictionFeedback } from "./PredictionFeedback";
import {
  citedPassageMissing,
  citedRevisionChanged,
  citedRevisionLabel,
  citedRevisionUnavailable,
} from "../automation-records/citationPin";

const DEMO_POLICY_LABEL = "Policy v14";

// Left nav plus the page's own gutters, in px.
const APP_CHROME_INSET = 260;

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.08em",
  color: "text.secondary",
  textTransform: "uppercase",
};

// Each half of the split scrolls on its own, so the CR's data and its ground
// truth stay beside the field cards however far down the cards you read. The
// cap is viewport-relative so the whole split fits on screen once expanded.
const paneSx = {
  overflowY: "auto",
  overscrollBehavior: "contain",
  maxHeight: { xs: "60vh", md: "min(72vh, 820px)" },
};

/** Small indigo citation-number circle, as in the mockup. */
const CiteNumber = ({ n, sx }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 16,
      height: 16,
      px: 0.25,
      borderRadius: "50%",
      backgroundColor: "#eef2ff",
      color: "#4f46e5",
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      ...sx,
    }}
  >
    {n}
  </Box>
);

CiteNumber.propTypes = {
  n: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

const useOpenReference = (reference, citationNumber) => {
  const { openCitation } = useDatasetViewerCitationPresentation();
  // Raw id, negative included: this is an API lookup, and a replicated
  // dataset's records live under the negative id. Only viewer ROUTES take the
  // canonical positive one, and openCitation normalizes that itself.
  const { data: datasetType } = useGetDatasetType(reference.dataset_id ?? null);
  // A deleted cited record has nothing to open.
  const clickable =
    !!reference.dataset_id && !citedRevisionUnavailable(reference);
  const open = () =>
    openCitation({
      citationNumber,
      datasetId: reference.dataset_id,
      datasetTypeId: datasetType?.id,
      sourceUid: reference.source_uid ?? undefined,
      sdrId: reference.sdr_id ?? undefined,
      // The quoted passage, when the server could still locate it.
      chunkId: reference.pin_status?.jump_chunk_id ?? undefined,
      // page_number is routinely an ARRAY in chunk metadata (page_span writes
      // [start, end]); wrapping it again nests it and the citation viewer's
      // pageNumber[0] then yields the inner array.
      pageNumber:
        reference.page_number != null
          ? Array.isArray(reference.page_number)
            ? reference.page_number
            : [reference.page_number]
          : undefined,
    });
  return { clickable, open };
};

/** Tooltip text for a pill: the cited revision and any change since. */
const sourceChipTitle = (reference, clickable) => {
  const revision = citedRevisionLabel(reference.pin);
  const parts = revision ? [`Cited ${revision}`] : [];
  if (reference.pin_status?.state === "superseded") {
    parts.push("A newer version exists");
  }
  if (citedPassageMissing(reference)) {
    parts.push("The quoted text can no longer be located in this version");
  }
  if (citedRevisionChanged(reference)) {
    parts.push("The cited record has changed since it was cited");
  }
  if (citedRevisionUnavailable(reference)) {
    parts.push("The cited record was deleted");
  } else if (!clickable) {
    parts.push("No linked dataset record");
  }
  return parts.join(". ");
};

/** Numbered source pill used inside field cards. */
const FieldSourceChip = ({ reference, number }) => {
  const { clickable, open } = useOpenReference(reference, number);
  const warn =
    citedRevisionUnavailable(reference) || citedRevisionChanged(reference);
  const title = sourceChipTitle(reference, clickable);
  const pill = (
    <Chip
      size="small"
      variant="outlined"
      color={warn ? "warning" : "default"}
      data-pin-state={reference.pin_status?.state}
      sx={{ backgroundColor: "#fff" }}
      icon={<CiteNumber n={number} sx={{ ml: 0.5 }} />}
      label={reference.description}
      clickable={clickable}
      onClick={clickable ? open : undefined}
      data-testid={`cap2-source-chip-${number}`}
    />
  );
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill;
};

FieldSourceChip.propTypes = {
  reference: PropTypes.object.isRequired,
  number: PropTypes.number.isRequired,
};

const PinnedVersionsBar = ({ llmDetails, policyLabel }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      mb: 1.5,
      px: 1.25,
      py: 0.5,
      borderRadius: 5,
      backgroundColor: "#eef0f4",
      fontSize: 12,
      color: "text.secondary",
    }}
    data-testid="cap2-pinned-bar"
  >
    <span>◇ Prediction pinned to</span>
    {llmDetails?.platform_version && (
      <b>Platform {llmDetails.platform_version}</b>
    )}
    {llmDetails?.model_id != null && (
      <>
        <span>·</span>
        <b>
          Model {llmDetails.model_id} v{llmDetails.model_version_number}
        </b>
      </>
    )}
    <span>·</span>
    <Tooltip title="Demo data - policy versioning is not live yet">
      <b data-testid="cap2-policy-chip">{policyLabel}</b>
    </Tooltip>
  </Box>
);

PinnedVersionsBar.propTypes = {
  llmDetails: PropTypes.object,
  policyLabel: PropTypes.string.isRequired,
};

/** Key/value rows for a record's raw field data, as in the production detail. */
const FieldValueTable = ({ data }) => {
  const entries = Object.entries(data || {}).filter(
    ([, value]) => value !== null && value !== "",
  );
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No data on this record.
      </Typography>
    );
  }
  return (
    <Box>
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            gap: 1,
            py: 0.5,
            borderTop: "1px solid",
            borderColor: "divider",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="body2"
            sx={{ flex: "0 0 34%", color: "text.secondary" }}
          >
            {key}
          </Typography>
          <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
            {typeof value === "object" ? JSON.stringify(value) : String(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

FieldValueTable.propTypes = { data: PropTypes.object };

/**
 * Ground truth for this CR. Collapsed by default: the values that decide
 * whether a prediction is correct are already shown on each field card, so
 * the raw panel is reference material rather than something read every time.
 */
const GroundTruthPanel = ({ data }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 1.5 }} data-testid="cap2-ground-truth-panel">
      <Button
        size="small"
        onClick={() => setOpen((prior) => !prior)}
        startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: "none", px: 0.5 }}
        aria-expanded={open}
        data-testid="cap2-ground-truth-toggle"
      >
        Ground truth data
      </Button>
      <Collapse in={open} unmountOnExit>
        {data && Object.keys(data).length > 0 ? (
          <FieldValueTable data={data} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No ground truth data uploaded for this record.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

GroundTruthPanel.propTypes = {
  data: PropTypes.object,
};

// Correctness is scored per field against the linked ground-truth dataset:
// green when the prediction matches, red when it does not, blue when the pair
// is out of the score at all - no ground-truth value, or no CAP 2.0
// prediction. The label carries the verdict in text, so colour is never the
// only signal.
const VERDICT_STYLES = {
  correct: {
    label: "Matches ground truth",
    swatch: "#2e7d32",
    bg: "#f1f8f2",
    border: "#a5d6a7",
  },
  incorrect: {
    label: "Differs from ground truth",
    swatch: "#c62828",
    bg: "#fdf1f0",
    border: "#ef9a9a",
  },
  unscored: {
    label: "Not scored",
    swatch: "#1565c0",
    bg: "#f1f6fd",
    border: "#90caf9",
  },
};

// Label sets, built exactly as the scorer behind the accuracy header builds
// them (ground_truth_labels_from_record_data / extract_prediction_labels in
// mre/products/sc_auto/shadow_ops.py): a list or a scalar, each entry
// stringified and trimmed, blanks dropped. No delimiter splitting and no
// case folding — the two sides draw from one allowed-labels vocabulary.
const labelSet = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return new Set(
    values
      .filter((item) => item != null)
      .map((item) => String(item).trim())
      .filter(Boolean),
  );
};

const sameLabels = (left, right) =>
  left.size === right.size && [...left].every((label) => right.has(label));

// Set equality for EVERY label type, as compute_shadow_accuracy scores it:
// a card that graded more loosely than the header above it read green on a
// record the header counted wrong. A field CAP 2.0 never predicted is out of
// the score entirely there (`if field in cap2`), so it is not wrong here.
const gradePrediction = (prediction, truthValue) => {
  if (!prediction) {
    return "unscored";
  }
  const truth = labelSet(truthValue);
  if (truth.size === 0) {
    return "unscored";
  }
  return sameLabels(labelSet(prediction.labels), truth)
    ? "correct"
    : "incorrect";
};

// A list ground truth reads as the field cards render predictions, not as
// Array.prototype.toString's comma run-on.
const truthText = (value) =>
  Array.isArray(value) ? value.join("; ") : String(value);

const FieldCard = ({ field, row, detailPrediction, numberFor, truthValue }) => {
  const values = row.cap2_predictions?.[field.model_output_name];
  const divergence = row.divergence_detail?.[field.model_output_name];
  const references = detailPrediction?.references || [];
  const predictedLabels = values?.labels || [];
  const verdict = gradePrediction(values, truthValue);
  const hasTruthValue = labelSet(truthValue).size > 0;
  const style = VERDICT_STYLES[verdict];
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        flex: "1 1 100%",
        backgroundColor: style.bg,
        borderColor: style.border,
      }}
      data-testid={`cap2-field-card-${field.model_output_name}`}
      data-verdict={verdict}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              backgroundColor: style.swatch,
              flexShrink: 0,
            }}
            aria-hidden="true"
          />
          <Typography sx={sectionLabelSx}>{field.name}</Typography>
          <Typography
            sx={{ ...sectionLabelSx, color: style.swatch }}
            data-testid={`cap2-field-verdict-${field.model_output_name}`}
          >
            {style.label}
          </Typography>
        </Box>
        {divergence && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Δ CAP 1.0 said ${divergence.cap1.join("; ") || "(none)"}`}
            data-testid={`cap2-field-delta-${field.model_output_name}`}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 3, my: 0.5 }}>
        <Box>
          <Typography sx={sectionLabelSx}>Predicted</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {predictedLabels.join("; ") || "-"}
          </Typography>
        </Box>
        <Box>
          <Typography sx={sectionLabelSx}>Ground truth</Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.3,
              color: hasTruthValue ? "text.primary" : "text.disabled",
            }}
            data-testid={`cap2-field-truth-${field.model_output_name}`}
          >
            {hasTruthValue ? truthText(truthValue) : "no value"}
          </Typography>
        </Box>
      </Box>
      {detailPrediction?.justification && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {detailPrediction.justification}
        </Typography>
      )}
      {references.length > 0 && (
        <>
          <Typography sx={{ ...sectionLabelSx, mb: 0.5 }}>Sources</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {references.map((reference, index) => (
              <FieldSourceChip
                key={index}
                reference={reference}
                number={numberFor(reference)}
              />
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};

FieldCard.propTypes = {
  field: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
  detailPrediction: PropTypes.object,
  truthValue: PropTypes.any,
  numberFor: PropTypes.func.isRequired,
};

/**
 * Expanded-row detail for the beta audit table, styled to the mockup:
 * pinned-version pill, record-level reasoning with numbered citation
 * circles, per-field cards with numbered source pills and divergence chips,
 * the searched/read/cited funnel, and the numbered sources panel with
 * open-links through Source Provenance.
 */
export const Cap2RecordDetail = ({
  row,
  fields,
  policyLabel,
  groundTruthDatasetId,
  templateId,
}) => {
  const { data: detail, isLoading } = useAutomationRecordDetail(
    row.cap2_record_id,
    row.cap2_record_id != null,
  );
  // Fetched once here rather than inside the collapsed panel: every field card
  // grades its prediction against these values, so they are needed whether or
  // not the reader ever opens the raw ground-truth table.
  // Null sourceUids is the hook's only disable switch, so it must carry the
  // missing-dataset and unscreened-row cases too: the id alone does not gate
  // the fetch, and the early return below means unscreened rows never use it.
  // The id goes through RAW: /datasets/{id}/multirecords filters on
  // Dataset.id, and a replicated dataset's records are stored under its
  // negative id, so abs() either 404s or reads another dataset's rows.
  const { data: groundTruthRecords } = useSubsetDetailedPOCRecords(
    groundTruthDatasetId ?? null,
    groundTruthDatasetId != null && row.cap2_record_id != null
      ? [row.source_uid]
      : null,
  );
  const groundTruth = groundTruthRecords?.[0]?.data || {};
  if (row.cap2_record_id == null) {
    return (
      <Alert severity="info" sx={{ m: 1 }}>
        No CAP 2.0 screening for this record yet
        {row.last_error ? ` - last error: ${row.last_error}` : "."}
      </Alert>
    );
  }
  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <CircularProgress size={20} data-testid="cap2-detail-loading" />
      </Box>
    );
  }
  const llmDetails = detail?.llm_details || {};
  const predictions = detail?.automated_data_record_predictions || [];
  // One number per distinct reference across the whole detail: the record-level
  // list is numbered first, then any field reference it does not contain. An
  // index into the record-level list alone gave every reference missing from it
  // the SAME number, and that list is no longer rendered to index into.
  const citationNumbers = new Map();
  [
    ...(llmDetails.overall_references || []),
    ...predictions.flatMap((prediction) => prediction.references || []),
  ].forEach((reference) => {
    if (!citationNumbers.has(reference.description)) {
      citationNumbers.set(reference.description, citationNumbers.size + 1);
    }
  });
  const numberFor = (reference) => citationNumbers.get(reference.description);
  const predictionsByClass = {};
  predictions.forEach((prediction) => {
    if (prediction.model_label_class_name) {
      predictionsByClass[prediction.model_label_class_name] = prediction;
    }
  });
  const detailFor = (field) =>
    predictionsByClass[field.name] ||
    predictionsByClass[field.model_output_name];
  // model_output_name only, as the backend scorer keys it: a display name
  // that happens to match some other column would score the wrong field.
  const truthFor = (field) => groundTruth[field.model_output_name];

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#f7f8fa",
        borderTop: "1px solid",
        borderBottom: "1px solid",
        borderColor: "divider",
        // Stays glued to the scrollport's left edge, so the whole detail is
        // readable at any horizontal scroll position of the table above it.
        position: "sticky",
        left: 0,
        // A visual cap, not a layout contract: the app chrome's width is a
        // Sass variable ($sidebar-width) with no JS/CSS-variable counterpart,
        // and the nav collapses, so this only has to stay off the right edge.
        width: { xs: "auto", md: `calc(100vw - ${APP_CHROME_INSET}px)` },
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
      data-testid={`cap2-detail-${row.source_uid}`}
    >
      <PinnedVersionsBar
        llmDetails={llmDetails}
        policyLabel={policyLabel || DEMO_POLICY_LABEL}
      />
      {/* Per-field predictions on the left, the CR itself on the right, each
          half scrolling independently: reading to the last field card no
          longer scrolls the record's data and ground truth out of view.
          alignItems stretch (the flex default) keeps the two panes the same
          height, so they read as one block whichever is longer. */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <Box
          sx={{
            flex: "1 1 50%",
            minWidth: 0,
            display: "flex",
            flexWrap: "wrap",
            alignContent: "flex-start",
            gap: 1.5,
            pr: 0.5,
            ...paneSx,
          }}
          data-testid="cap2-predictions-pane"
        >
          {fields.map((field) => (
            <FieldCard
              key={field.model_output_name}
              field={field}
              row={row}
              detailPrediction={detailFor(field)}
              numberFor={numberFor}
              truthValue={truthFor(field)}
            />
          ))}
        </Box>
        <Paper
          variant="outlined"
          sx={{
            flex: "1 1 50%",
            minWidth: 0,
            px: 1.5,
            // The heading supplies the top padding itself so it can sit flush
            // with the scrollport and pin there; see its sx below.
            pt: 0,
            pb: 1.5,
            backgroundColor: "#fff",
            ...paneSx,
          }}
          data-testid="cap2-record-data"
        >
          {/* Pinned to the top of its own scrollport: the pane keeps its
              heading once the field rows scroll under it. */}
          <Typography
            sx={{
              ...sectionLabelSx,
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "#fff",
              mx: -1.5,
              px: 1.5,
              pt: 1.5,
              pb: 0.5,
            }}
          >
            Data provided for automation
          </Typography>
          <FieldValueTable data={detail?.source_record_data} />
          {groundTruthDatasetId != null && (
            <GroundTruthPanel data={groundTruth} />
          )}
        </Paper>
      </Box>
      {templateId != null && (
        <PredictionFeedback
          templateId={templateId}
          sourceUid={row.source_uid}
        />
      )}
    </Box>
  );
};

Cap2RecordDetail.propTypes = {
  row: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  policyLabel: PropTypes.string,
  groundTruthDatasetId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Cap2RecordDetail;
